import type { EmailChrome } from "../../components/emailTemplateEditor/builder/core/types";

export const BODY_START_MARKER = "<!-- body start -->";
export const BODY_END_MARKER = "<!-- body end -->";
const CHROME_MARKER = "<!-- hissa-editor-chrome:";

export type TemplateToken = { variable: string; exampleValue: string };
export type { EmailChrome };

export function isAmpEmailTemplate(templateType?: string): boolean {
  return templateType === "GrantApprovalAmp";
}

export const DEFAULT_EMAIL_CHROME: EmailChrome = {
  brand: {
    name: "Hissa",
    logoInitial: "H",
    accentColor: "#E85936",
    darkColor: "#0C224A",
  },
  headerSubtitle: "Grant Acceptance Reminder",
  factsRows: [
    { label: "Plan", value: "#plan_name#" },
    { label: "Options", value: "#granted_qty#" },
    { label: "Accept by", value: "#deadline#" },
  ],
  cta: {
    label: "Review & Accept Your Grant",
    url: "#grant_acceptance_url#",
  },
  footer: {
    learnMore: {
      label: "Learn more about ESOPs",
      url: "#learn_more_url#",
    },
    note: "Confidential. Intended only for #employee_name#.\nQuestions? Contact your HR team.",
    copyrightText: "Copyright © Rulezero, All rights reserved.",
    trademarkText: "Hissa is a registered trademark of Rulezero.",
    unsubscribe: { label: "Unsubscribe", url: "#unsubscribe_url#" },
  },
  poweredByLabel: "Hissa",
};

function cloneDefaultChrome(): EmailChrome {
  return JSON.parse(JSON.stringify(DEFAULT_EMAIL_CHROME));
}

function encodeChrome(chrome: EmailChrome) {
  return btoa(unescape(encodeURIComponent(JSON.stringify(chrome))));
}

function decodeChrome(encoded: string): EmailChrome | null {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(encoded)))) as EmailChrome;
  } catch {
    return null;
  }
}

export function getTemplateBody(html: string): string | null {
  const start = html.indexOf(BODY_START_MARKER);
  const end = html.indexOf(BODY_END_MARKER);
  if (start < 0 || end < 0 || end <= start) return null;
  return html.slice(start + BODY_START_MARKER.length, end);
}

export function getTemplateChrome(html: string): EmailChrome {
  const match = html.match(/<!-- hissa-editor-chrome:([\s\S]*?) -->/);
  return (match && decodeChrome(match[1])) || cloneDefaultChrome();
}

export function addTemplateEditorMarkers(
  html: string,
  body: string,
  chrome: EmailChrome
): string {
  const bodyIndex = html.indexOf(body);
  if (bodyIndex < 0) return html;
  const withBody = `${html.slice(
    0,
    bodyIndex
  )}${BODY_START_MARKER}${body}${BODY_END_MARKER}${html.slice(
    bodyIndex + body.length
  )}`;
  return `${withBody}\n${CHROME_MARKER}${encodeChrome(chrome)} -->`;
}

/** Converts abandoned editor-only aliases without ever generating them. */
export function replaceLegacyAliases(value: string, tokens: TemplateToken[]) {
  return tokens.reduce(
    (result, token, index) =>
      result.split(`{{legacy_${index}}}`).join(token.variable),
    value
  );
}
